import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MdAdd } from 'react-icons/md';
import { IconButton, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { Action } from 'helpers';
import { useStyles } from './styles';
import SearchWithFilters from 'components/SearchWithFilters';
import { tableStyles } from 'components/Tables/TableCells/styles';
import { debounce } from 'GeminiViewerComponent/helpers/lodashUtils';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { makeButtonStyles } from '../../../../GeminiViewerComponent/components/styles';
import { ResizableTable } from 'GeminiViewerComponent/components/Tables/ResizableTable';
import { EnhancedTableToolbar } from 'GeminiViewerComponent/components/EnhancedTableToolbar';
import { closePanel, openPanel, setEditObjectId } from 'features/common/formSlice';
import {
    resizableLabelHeader,
    resizableLabelCell,
    resizableActionCell,
    resizableCheckCell,
    resizableCheckHeaderCell,
    resizableCategoryTypeCell,
} from 'components';
import { usePrevious } from 'hooks/usePrevious';

const CategoryTable = ({
    categories,
    pageInfo,
    categoryPayload,
    setCategoryPayload,
    sortDirection,
    sortBy,
    handleSort,
}) => {
    //#region Constants
    const headerHeight = 40;
    const rowHeight = 60;
    //#endregion Constants

    //#region Hooks
    const dispatch = useDispatch();
    const classes = useStyles();
    const tableClasses = tableStyles();
    //#endregion Hooks

    //#region State
    const [selected, setSelected] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);
    //#endregion State

    //#region Selectors
    const theme = useSelector(selectDefaultTheme);
    const buttonStyles = makeButtonStyles(theme);
    const loadedRowsMap = useSelector((state) => state.category.loadedCategoryMap);
    //#endregion Selectors

    //#region Refs
    //#endregion Refs

    //#region Effects
    //#endregion Effects

    //#region Methods
    const isSelected = (id) => selected.indexOf(id) !== -1;
    const prevSelectedAll = usePrevious(selectedAll);
    if (prevSelectedAll && selectedAll) {
        if (selected.length !== categories.length) {
            setSelected(categories.map((row) => row.category_id));
            setSelectedAll(true);
        }
    }

    const handleClick = (event, category_id) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(category_id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, category_id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
        setSelectedAll(newSelected.length === categories.length);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelectedIds = categories.map((category) => category.category_id);
            setSelected(newSelectedIds);
            setSelectedAll(true);
            return;
        }
        setSelected([]);
        setSelectedAll(false);
    };

    const handleRowClick = async ({ rowData }) => {
        await dispatch(
            closePanel({
                formKey: 'categoryForm',
            })
        );
        if (rowData?.category_id) {
            await dispatch(
                setEditObjectId({
                    formKey: `categoryForm`,
                    object_id: rowData?.category_id,
                })
            );
            dispatch(
                openPanel({
                    formKey: `categoryForm`,
                    formAction: 'Edit',
                })
            );
        }
    };

    const columns = [
        {
            accessorKey: 'category_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            label: 'Select',
            padding: 'checkbox',
            cellLevel: 'category',
            numSelected: selected.length,
            rowCount: categories.length,
            handleClick: handleClick,
            handleSelectAllClick: handleSelectAllClick,
            isSelected: isSelected,
            header: (info) => resizableCheckHeaderCell(null, info?.header?.column?.columnDef),
            cell: (info) => resizableCheckCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'category_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            padding: 'none',
            label: '',
            actionCellLevel: 'category',
            targetPopup: 'categoryManagement',
            formKey: 'categoryForm',
            dispatch: dispatch,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableActionCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            flexGrow: 1,
            accessorKey: 'display_name',
            size: 250,
            minSize: 250,
            label: 'Category',
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'asset_type_id',
            flexGrow: 1,
            size: 100,
            minSize: 100,
            label: 'Type',
            enableResizing: false,
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableCategoryTypeCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'asset_count',
            flexGrow: 1,
            size: 100,
            minSize: 100,
            label: 'N° Assets',
            enableResizing: false,
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
    ];

    function loadMoreRows({ startIndex, stopIndex }) {}

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((searchString) => {
            setCategoryPayload({
                ...categoryPayload,
                searchString: searchString,
            });
        }, 1000),
        [categoryPayload]
    );

    const handleCreateNew = () => {
        dispatch(
            openPanel({
                formKey: 'categoryForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    };
    //#endregion Methods

    //#region Render time calcs
    //#endregion Render time calcs

    //#region Render
    return (
        <div className={classes.root}>
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <Tooltip title="Add New Category">
                    <IconButton onClick={handleCreateNew} size="large" className={buttonStyles.contentAddButton}>
                        <MdAdd className="react-icon" />
                    </IconButton>
                </Tooltip>
                <div style={{ flexGrow: 1 }}></div>
                <SearchWithFilters filter={false} onChange={(evt) => debouncedSearch(evt.target.value)} />
            </div>
            {selected.length > 0 && (
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    toolTipTitle={'Delete'}
                    totalCount={pageInfo.TotalCount}
                >
                    <PopupAction
                        action={Action.DeleteArray}
                        object={{
                            categoryIds: selected,
                            setCategoryIds: setSelected,
                        }}
                        level={'category'}
                        showLabel={false}
                    />
                </EnhancedTableToolbar>
            )}
            <div
                style={{
                    height: `calc(100vh - ${selected.length > 0 ? '345px' : '280px'})`,
                    minWidth: '500px',
                }}
            >
                <ResizableTable
                    initialRows={categories}
                    allRows={categories}
                    totalRowCount={pageInfo.TotalCount}
                    loadedRowsMap={loadedRowsMap}
                    loadMoreRows={loadMoreRows}
                    columns={columns}
                    cellClassName={tableClasses.flexContainer}
                    className={tableClasses.table}
                    rowClassName={tableClasses.flexContainer}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    onRowClick={handleRowClick}
                />
            </div>
        </div>
    );
    //#endregion Render
};

export { CategoryTable };
